import React, { useState, useMemo } from 'react'

import { TableSortLabel, Theme, Tooltip } from '@material-ui/core'
import { Pagination } from '@material-ui/lab'

import { Container } from '../../../components'
import { UnitInspectionCompletionData } from '../../../hooks'
import { toMMDDYYYY } from '../../../helpers'
import { usePagination } from '../../../hooks'
import { typographyStyle } from '../../../theme/typographyStyle'

interface Props {
    data: UnitInspectionCompletionData[] | null
    theme: Theme
    searchTerm: string
}

type SortField =
    | 'completed_date'
    | 'inspection_name'
    | 'inspection_type'
    | 'completed_by_name'
    | 'unit_name'
    | 'start_date'
    | 'end_date'
type SortDirection = 'asc' | 'desc'

export const UnitInspectionCompletionTable = (props: Props) => {
    const { data, theme, searchTerm } = props
    const [sortField, setSortField] = useState<SortField>('completed_date')
    const [sortDirection, setSortDirection] = useState<SortDirection>('desc')

    const columnWidths = {
        inspection_name: theme.spacing(30),
        inspection_type: theme.spacing(20),
        unit_name: theme.spacing(20),
        completed_by_name: theme.spacing(25),
        completed_date: theme.spacing(20),
        start_date: theme.spacing(20),
        end_date: theme.spacing(20),
    }

    const headCellStyle = (field: SortField): React.CSSProperties => ({
        ...typographyStyle(theme.typography.h6),
        fontWeight: 600,
        color: theme.palette.darkGreen.main,
        whiteSpace: 'pre-line',
        padding: theme.spacing(1, 2),
        width: columnWidths[field],
        minWidth: columnWidths[field],
        display: 'flex',
        justifyContent: 'flex-end',
        flexShrink: 0,
        flex: 1,
    })

    const cellStyle = (field: SortField): React.CSSProperties => ({
        ...typographyStyle(theme.typography.subtitle1),
        fontWeight: 400,
        padding: theme.spacing(1, 2),
        whiteSpace: 'nowrap',
        width: columnWidths[field],
        minWidth: columnWidths[field],
        display: 'flex',
        justifyContent: 'flex-end',
        flexShrink: 0,
        flex: 1,
    })

    const handleSort = (field: SortField) => {
        if (sortField === field) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
        } else {
            setSortField(field)
            setSortDirection('desc')
        }
    }

    // Memoize the filtered and sorted data
    const filteredAndSortedData = useMemo(() => {
        if (!data) return []

        // First filter the data
        const filtered = data.filter((item) =>
            Object.values(item).some((value) =>
                value
                    ?.toString()
                    .toLowerCase()
                    .includes(searchTerm.toLowerCase()),
            ),
        )

        // Then sort it
        return [...filtered].sort((a, b) => {
            const multiplier = sortDirection === 'asc' ? 1 : -1

            if (
                sortField === 'completed_date' ||
                sortField === 'start_date' ||
                sortField === 'end_date'
            ) {
                const dateA = new Date(a[sortField])
                const dateB = new Date(b[sortField])
                return multiplier * (dateA.getTime() - dateB.getTime())
            }

            return multiplier * a[sortField].localeCompare(b[sortField])
        })
    }, [data, searchTerm, sortField, sortDirection])

    const { page, setPage, pageData, numPages, start, end } = usePagination(
        filteredAndSortedData,
    )

    return (
        <Container direction="column" style={{ flex: 1, overflow: 'hidden' }}>
            <Container
                style={{
                    flex: 1,
                    overflowX: 'auto',
                    overflowY: 'hidden',
                    width: '100%',
                }}
            >
                <Container
                    direction="column"
                    style={{
                        minWidth: 'fit-content',
                        flex: 1,
                        width: '100%',
                    }}
                >
                    {/* Headers */}
                    <Container
                        style={{
                            padding: theme.spacing(1),
                            borderBottom: '1px solid',
                            borderColor: theme.palette.grey[400],
                            flexShrink: 0,
                            display: 'flex',
                            flexDirection: 'row',
                            width: '100%',
                        }}
                    >
                        <Container style={headCellStyle('inspection_name')}>
                            <TableSortLabel
                                active={sortField === 'inspection_name'}
                                direction={sortDirection}
                                onClick={() => handleSort('inspection_name')}
                            >
                                Inspection Name
                            </TableSortLabel>
                        </Container>
                        <Container style={headCellStyle('inspection_type')}>
                            <TableSortLabel
                                active={sortField === 'inspection_type'}
                                direction={sortDirection}
                                onClick={() => handleSort('inspection_type')}
                            >
                                Inspection Type
                            </TableSortLabel>
                        </Container>
                        <Container style={headCellStyle('unit_name')}>
                            <TableSortLabel
                                active={sortField === 'unit_name'}
                                direction={sortDirection}
                                onClick={() => handleSort('unit_name')}
                            >
                                Unit
                            </TableSortLabel>
                        </Container>
                        <Container style={headCellStyle('completed_by_name')}>
                            <TableSortLabel
                                active={sortField === 'completed_by_name'}
                                direction={sortDirection}
                                onClick={() => handleSort('completed_by_name')}
                            >
                                Completed By
                            </TableSortLabel>
                        </Container>
                        <Container style={headCellStyle('completed_date')}>
                            <TableSortLabel
                                active={sortField === 'completed_date'}
                                direction={sortDirection}
                                onClick={() => handleSort('completed_date')}
                            >
                                Completed Date
                            </TableSortLabel>
                        </Container>
                        <Container style={headCellStyle('start_date')}>
                            <TableSortLabel
                                active={sortField === 'start_date'}
                                direction={sortDirection}
                                onClick={() => handleSort('start_date')}
                            >
                                Start Date
                            </TableSortLabel>
                        </Container>
                        <Container style={headCellStyle('end_date')}>
                            <TableSortLabel
                                active={sortField === 'end_date'}
                                direction={sortDirection}
                                onClick={() => handleSort('end_date')}
                            >
                                End Date
                            </TableSortLabel>
                        </Container>
                    </Container>

                    {/* Data Rows */}
                    <Container
                        style={{
                            flex: 1,
                            overflowY: 'auto',
                        }}
                        direction="column"
                    >
                        {pageData.map((item, index) => (
                            <Container
                                key={`unit-inspection-${index}`}
                                style={{
                                    borderBottom: '1px solid',
                                    borderColor: theme.palette.grey[400],
                                    display: 'flex',
                                    flexDirection: 'row',
                                    backgroundColor:
                                        index % 2 === 0
                                            ? theme.palette.grey[100]
                                            : 'white',
                                    width: '100%',
                                }}
                            >
                                <Container style={cellStyle('inspection_name')}>
                                    <Tooltip
                                        title={item.inspection_name}
                                        enterDelay={1000}
                                    >
                                        <div
                                            style={{
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                            }}
                                        >
                                            {item.inspection_name}
                                        </div>
                                    </Tooltip>
                                </Container>
                                <Container style={cellStyle('inspection_type')}>
                                    <Tooltip
                                        title={item.inspection_type}
                                        enterDelay={1000}
                                    >
                                        <div
                                            style={{
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                            }}
                                        >
                                            {item.inspection_type}
                                        </div>
                                    </Tooltip>
                                </Container>
                                <Container style={cellStyle('unit_name')}>
                                    <Tooltip
                                        title={item.unit_name}
                                        enterDelay={1000}
                                    >
                                        <div
                                            style={{
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                            }}
                                        >
                                            {item.unit_name}
                                        </div>
                                    </Tooltip>
                                </Container>
                                <Container
                                    style={cellStyle('completed_by_name')}
                                >
                                    <Tooltip
                                        title={item.completed_by_name}
                                        enterDelay={1000}
                                    >
                                        <div
                                            style={{
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                            }}
                                        >
                                            {item.completed_by_name}
                                        </div>
                                    </Tooltip>
                                </Container>
                                <Container style={cellStyle('completed_date')}>
                                    <Tooltip
                                        title={toMMDDYYYY(
                                            new Date(item.completed_date),
                                        )}
                                        enterDelay={1000}
                                    >
                                        <div
                                            style={{
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                            }}
                                        >
                                            {toMMDDYYYY(
                                                new Date(item.completed_date),
                                            )}
                                        </div>
                                    </Tooltip>
                                </Container>
                                <Container style={cellStyle('start_date')}>
                                    <Tooltip
                                        title={toMMDDYYYY(
                                            new Date(item.start_date),
                                        )}
                                        enterDelay={1000}
                                    >
                                        <div
                                            style={{
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                            }}
                                        >
                                            {toMMDDYYYY(
                                                new Date(item.start_date),
                                            )}
                                        </div>
                                    </Tooltip>
                                </Container>
                                <Container style={cellStyle('end_date')}>
                                    <Tooltip
                                        title={toMMDDYYYY(
                                            new Date(item.end_date),
                                        )}
                                        enterDelay={1000}
                                    >
                                        <div
                                            style={{
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                            }}
                                        >
                                            {toMMDDYYYY(
                                                new Date(item.end_date),
                                            )}
                                        </div>
                                    </Tooltip>
                                </Container>
                            </Container>
                        ))}
                    </Container>
                </Container>
            </Container>

            {/* Pagination */}
            <Container
                style={{
                    ...typographyStyle(theme.typography.subtitle1),
                    borderTop: `1px solid ${theme.palette.grey[400]}`,
                    padding: theme.spacing(1),
                    justifyContent: 'center',
                    alignItems: 'center',
                    flexShrink: 0,
                    backgroundColor: 'white',
                    width: '100%',
                }}
            >
                <Pagination
                    count={numPages}
                    page={page}
                    onChange={(_, newPage) => setPage(newPage)}
                />
                <span style={{ marginLeft: theme.spacing(2) }}>
                    Showing {start} - {end} of {filteredAndSortedData.length}{' '}
                    {searchTerm && '(filtered)'}
                </span>
            </Container>
        </Container>
    )
}
